// client/src/components/ScoreboardOverlay.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Text,
  SimpleGrid,
  HStack,
  Image,
  Flex,
  Icon,
  useToast,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerFooter,
  Button,
  useDisclosure,
  DrawerCloseButton,
} from '@chakra-ui/react';
import {
  FaCirclePlus,
  FaCircleMinus,
  FaBars,
  FaDoorOpen,
} from 'react-icons/fa6';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import throttle from 'lodash.throttle';
import io from 'socket.io-client';

// Import your images/constants
import { BG_IMAGES, COLORS } from '../../constants';

const MotionBox = motion.create(Box);

// Dev: CRA proxies /socket.io → cil-server (see setupProxy.js). Prod: same origin.
// In dev (CRA + HTTPS) il WebSocket via proxy fallisce: solo polling, come virtualSpaceSocket.
const inDev = process.env.NODE_ENV === 'development';
const socket = io({
  path: '/socket.io',
  withCredentials: true,
  transports: inDev ? ['polling'] : ['polling', 'websocket'],
  upgrade: !inDev,
});

/**
 * Define custom rotation logic based on total participants and their index.
 * Modify these rules as you see fit (this is just a starting example!).
 */
function getRotation(index, total) {
  // 2 participants => Opposite rotation
  if (total === 2) {
    return index === 0 ? 'rotate(180deg)' : 'none';
  }
  // 3 participants => 1st rotates 180°, next two normal
  if (total === 3) {
    if (index === 0) return 'rotate(180deg)';
    if (index === 1) return 'rotate(180deg)';
    if (index === 2) return 'none';
    return 'rotate(90deg)';
  }
  // 4 participants => top row rotates 180°, bottom row normal
  if (total === 4) {
    return index < 2 ? 'rotate(180deg)' : 'none';
  }
  // 5 participants => top row 180°, next row normal, last one 90°
  if (total === 5) {
    if (index < 2) return 'rotate(180deg)';
    if (index < 4) return 'none';
    return 'rotate(90deg)';
  }
  // 6 participants => top row 180°, bottom row normal
  if (total === 6) {
    return index < 3 ? 'rotate(180deg)' : 'none';
  }

  // Fallback: top row only
  return index < 2 ? 'rotate(180deg)' : 'none';
}

function ScoreboardOverlay({
  expandedGame,
  setExpandedGameId,
  onFinishGame,
  finishLoading,
}) {
  const [localParticipants, setLocalParticipants] = useState([]);
  const toast = useToast();

  // Drawer controls
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (expandedGame) {
      setLocalParticipants([...expandedGame.Participants]);
      socket.emit('joinGameRoom', expandedGame.documentId);

      // Funzione per attivare il fullscreen
      const requestFullscreen = () => {
        const elem = document.documentElement;
        if (elem.requestFullscreen) elem.requestFullscreen();
        else if (elem.mozRequestFullScreen) elem.mozRequestFullScreen();
        else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
        else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
      };

      requestFullscreen();
    } else {
      setLocalParticipants([]);
    }

    return () => {
      socket.emit('leaveGameRoom', expandedGame?.documentId);

      // Esci automaticamente dal fullscreen quando il componente viene smontato
      const exitFullscreen = () => {
        if (
          document.fullscreenElement ||
          document.mozFullScreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        ) {
          if (document.exitFullscreen) document.exitFullscreen();
          else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
          else if (document.webkitExitFullscreen)
            document.webkitExitFullscreen();
          else if (document.msExitFullscreen) document.msExitFullscreen();
        }
      };

      exitFullscreen();
    };
  }, []);

  const handleScoreUpdated = ({ playerId, newScore }) => {
    setLocalParticipants((prev) =>
      prev.map((p) => (p.playerId === playerId ? { ...p, score: newScore } : p))
    );
  };

  // Listen for real-time score updates
  useEffect(() => {
    socket.on('scoreUpdated', handleScoreUpdated);
    return () => {
      socket.off('scoreUpdated', handleScoreUpdated);
    };
  }, []);

  const handleScoreChange = useCallback(
    async (playerId, delta) => {
      if (!expandedGame?.documentId) return;
      try {
        const response = await axios.post(
          '/api/games/score',
          {
            gameId: expandedGame.documentId,
            playerId,
            delta,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
            },
          }
        );

        if (response.data) {
          handleScoreUpdated({
            playerId: response.data.playerId,
            newScore: response.data.newScore,
          });
        }
        // The local update is triggered by the socket event anyway
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating score:', error);
        toast({
          title: 'Error updating score',
          description: error?.response?.data?.error || error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [expandedGame, toast]
  );

  // Throttle to once every 300ms
  const throttledHandleScoreChange = useMemo(
    () =>
      throttle((playerId, delta) => handleScoreChange(playerId, delta), 300),
    [handleScoreChange]
  );

  // AnimatePresence transition config
  const transitionConfig = {
    duration: 0.24,
    ease: [0.68, -0.55, 0.265, 1.55],
  };

  // "Finish Game" logic
  const handleFinishGame = async () => {
    if (!expandedGame?.documentId) return;

    if (onFinishGame) {
      await onFinishGame(expandedGame.documentId);
    }
  };

  // Calculate how many columns we want for the grid
  const numParticipants = localParticipants.length;
  const columns = Math.ceil(Math.sqrt(numParticipants)) || 1;

  return (
    <AnimatePresence>
      {expandedGame && (
        <MotionBox
          position="fixed"
          top={0}
          left={0}
          width="100%"
          height="100%"
          zIndex={10000}
          bg="black"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={transitionConfig}
        >
          {/* 
            -- "Settings" Icon Button to open Drawer --
            Positioned in the vertical center, on the far right edge.
          */}
          <IconButton
            icon={<FaBars />}
            onClick={onOpen}
            position="absolute"
            top="50%"
            right="-20px"
            transform="translateY(-50%)"
            variant="solid"
            colorScheme="orange"
            aria-label="Open Menu"
            boxShadow="2xl"
            size="lg"
            zIndex={10000}
            justifyContent="flex-start"
            paddingLeft="6px"
            _active={{ transform: 'translateY(-50%) scale(.97)' }}
          />

          {/* 
            -- Drawer for Game Options --
            slides in from the right. 
          */}
          <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
            <DrawerOverlay />
            <DrawerContent
              bg="purple.100"
              borderTopLeftRadius="lg"
              borderBottomLeftRadius="lg"
            >
              <DrawerCloseButton
                top={3}
                right={3}
                size="md"
                variant="custom"
                bg="purple.500"
              />
              <DrawerBody pt={6}>
                <Text mb={4}>
                  <strong>Game Name:</strong> {expandedGame.Name}
                </Text>
                <Text mb={4}>
                  <strong>Format:</strong> {expandedGame.Format}
                </Text>
                <Text mb={4}>
                  <strong>Table:</strong> {expandedGame.Table_Name}
                </Text>

                {/* Additional game details, if desired */}
                <Text mb={4}>
                  <strong>Initial Score:</strong> {expandedGame.Initial_Score}
                </Text>
              </DrawerBody>
              <DrawerFooter borderTopWidth="1px" bg="purple.200">
                <Flex w="100%" justify="space-between" flexDirection="column">
                  {/* "Finish Game" button */}
                  <Button
                    colorScheme="red"
                    size="sm"
                    onClick={handleFinishGame}
                    _active={{ transform: 'scale(.97)' }}
                    isLoading={finishLoading}
                    disabled={finishLoading}
                  >
                    Termina Partita
                  </Button>
                  {/* "Close Overlay" button */}
                  <Button
                    leftIcon={<FaDoorOpen />}
                    variant="outline"
                    mt={2}
                    size="sm"
                    _active={{ transform: 'scale(.97)' }}
                    onClick={() => {
                      onClose();
                      setTimeout(() => {
                        setExpandedGameId(null);
                      }, 240);
                    }}
                  >
                    Chiudi Scoreboard
                  </Button>
                </Flex>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>

          {/* Main scoreboard grid */}
          <SimpleGrid columns={[1, columns]} spacing={0} height="100%">
            {localParticipants.map((participant, index) => {
              const backgroundURL = BG_IMAGES[(index % 4) + 1] || BG_IMAGES[0];
              return (
                <Box
                  key={participant.playerId}
                  position="relative"
                  backgroundImage={`url(${backgroundURL})`}
                  backgroundSize="cover"
                  backgroundPosition="center"
                  backgroundRepeat="no-repeat"
                  overflow="hidden"
                  // Use our custom rotation logic
                  transform={getRotation(index, numParticipants)}
                >
                  {/* Large Score in center */}
                  <Flex
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    align="center"
                    width="100%"
                    height="100%"
                    justify="center"
                  >
                    {/* Decrement Button */}
                    <Flex
                      flex={1}
                      justify="flex-end"
                      height="100%"
                      align="center"
                      onClick={() =>
                        !expandedGame.End_Time &&
                        throttledHandleScoreChange(participant.playerId, -1)
                      }
                      cursor="pointer"
                      _active={{ transform: 'scale(0.98)' }}
                    >
                      <Icon as={FaCircleMinus} fontSize="4xl" color="white" />
                    </Flex>

                    {/* Score Text */}
                    <Text
                      mx={6}
                      mt={-2}
                      fontSize={['4xl', '6xl', '7xl']}
                      fontWeight="bold"
                      color="white"
                      userSelect="none"
                    >
                      {participant.score}
                    </Text>

                    {/* Increment Button */}
                    <Flex
                      flex={1}
                      height="100%"
                      align="center"
                      onClick={() =>
                        !expandedGame.End_Time &&
                        throttledHandleScoreChange(participant.playerId, +1)
                      }
                      cursor="pointer"
                      _active={{ transform: 'scale(0.98)' }}
                    >
                      <Icon as={FaCirclePlus} fontSize="4xl" color="white" />
                    </Flex>
                  </Flex>

                  {/* Participant name in bottom-left */}
                  <Text
                    position="absolute"
                    bottom="0"
                    left="0"
                    fontSize="md"
                    fontWeight="semibold"
                    color="white"
                    p="4px 8px"
                    borderTopRightRadius="md"
                    userSelect="none"
                    bg="blackAlpha.600"
                  >
                    {participant.playerName || 'No Name'}
                  </Text>

                  {/* Color icons in bottom-right */}
                  {participant.colors && participant.colors.length > 0 && (
                    <HStack
                      position="absolute"
                      bottom="10px"
                      right="10px"
                      spacing={1}
                    >
                      {participant.colors.map((colorCode, i) => (
                        <Image
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${participant.playerId}-color-${i}`}
                          src={COLORS[colorCode]}
                          alt={colorCode}
                          boxSize="24px"
                        />
                      ))}
                    </HStack>
                  )}
                </Box>
              );
            })}
          </SimpleGrid>
        </MotionBox>
      )}
    </AnimatePresence>
  );
}

export default ScoreboardOverlay;
